
export interface DocParagraph {
  text: string;
  page: number;
}

export interface DocSection {
  id: string;
  title: string;
  level: number;
  page: number;
  paragraphs: DocParagraph[];
}

export interface DocTocEntry {
  id: string;
  title: string;
  level: number;
  page: number;
}

export interface ExtractedDoc {
  id: string;
  title: string;
  subtitle: string;
  source_filename: string;
  section_count: number;
  paragraph_count: number;
  toc: DocTocEntry[];
  sections: DocSection[];
}

export interface DocMeta {
  id: string;
  title: string;
  subtitle: string;
  /** Type of source — drives which viewer is used. */
  kind: "excel" | "pdf";
  /** Path under /docs/ — null for Excel which has a special view. */
  filePath: string;
  /** Approx file size in MB for UI display. */
  sizeMb: number;
  /** Lucide-style emoji label. Avoid emojis on prod surfaces. */
  pages?: number;
  /** Extracted structured form, null for Excel and external links. */
  extracted: ExtractedDoc | null;
  /** Source attribution label as it appears in cross-doc highlights. */
  highlightSourceLabel: string;
  /** External-only: open in new tab instead of in-app reader. */
  external?: boolean;
}

export const DOCS: DocMeta[] = [
  {
    id: "pm26-veiligheidsplan",
    title: "Veiligheidsplan Pal Mundo V4",
    subtitle: "SKILLS Crowd Management · 08 jul 2026 · scenario's §15",
    kind: "pdf",
    filePath: "/docs/pm26-veiligheidsplan.pdf",
    sizeMb: 6,
    extracted: null,
    highlightSourceLabel: "Veiligheidsplan PM26",
    external: true,
  },
  {
    id: "pm26-medisch-plan",
    title: "Medisch Plan v3.1",
    subtitle: "EMS · inzet 18 hulpverleners + reddingsbrigade · weersituatieschaal",
    kind: "pdf",
    filePath: "/docs/pm26-medisch-plan.pdf",
    sizeMb: 3,
    extracted: null,
    highlightSourceLabel: "Medisch plan PM26",
    external: true,
  },
  {
    id: "pm26-beveiligingsplan",
    title: "Beveiligingsplan CSS v2026.04",
    subtitle: "69 beveiligers · posten, IBT-teams, procedures",
    kind: "pdf",
    filePath: "/docs/pm26-beveiligingsplan.pdf",
    sizeMb: 3,
    extracted: null,
    highlightSourceLabel: "Beveiligingsplan PM26",
    external: true,
  },
  {
    id: "pm26-serviceplan",
    title: "Serviceplan CSSS",
    subtitle: "12 servicemedewerkers · nooduitgangen, frontstage, Golden Circle",
    kind: "pdf",
    filePath: "/docs/pm26-serviceplan.pdf",
    sizeMb: 2,
    extracted: null,
    highlightSourceLabel: "Serviceplan PM26",
    external: true,
  },
  {
    id: "pm26-verkeersplan",
    title: "Verkeers- en Vervoersplan v3.2",
    subtitle: "TTS · modal split, pendel Almere Poort, calamiteitenroutes CR1-3",
    kind: "pdf",
    filePath: "/docs/pm26-verkeersplan.pdf",
    sizeMb: 4,
    extracted: null,
    highlightSourceLabel: "Verkeersplan PM26",
    external: true,
  },
];

export function getDoc(id: string): DocMeta | undefined {
  return DOCS.find((d) => d.id === id);
}

// ── Kaarten ────────────────────────────────────────────────────────────────
// Web-weergaven (jpg ~2400px) van de Pal Mundo 2026-tekeningen.
// Bron: Drive-map "Pal Mundo 2026" (PMF26 v6.0 + stippenplan CSS + kaartenset TTS v3.2).
// Vertrouwelijk materiaal — alleen voor dit event-CP.

export interface KaartMeta {
  id: string;
  title: string;
  subtitle: string;
  /** Path under /kaarten/ (webp). */
  file: string;
  /** Optional original PDF for deep zoom. */
  pdf?: string;
}

export const KAARTEN: KaartMeta[] = [
  {
    id: "plattegrond",
    title: "Terreinplattegrond PMF26 v6.0 (blad 1)",
    subtitle: "Area's, entree, nooduitgangen, VIP Deck — dagelijks gebruik",
    file: "/kaarten/pm26-plattegrond-1.jpg",
    pdf: "/kaarten/pm26-plattegrond.pdf",
  },
  {
    id: "plattegrond-2",
    title: "Terreinplattegrond PMF26 v6.0 (blad 2 · DWG)",
    subtitle: "Detail: ticketscan 10+2VIP+3 fastlane, visitatie, bars, CP-locatie",
    file: "/kaarten/pm26-plattegrond-2.jpg",
    pdf: "/kaarten/pm26-plattegrond.pdf",
  },
  {
    id: "stippenplan",
    title: "Stippenplan beveiliging (CSS v2026.04)",
    subtitle: "Statische posten 1-28 — wie staat waar",
    file: "/kaarten/pm26-stippenplan.jpg",
  },
  {
    id: "verkeer-1",
    title: "Verkeer 1 · Bordenplan (TTS)",
    subtitle: "P1 Olympiakwartier · P2+Crew 1 Muiderzandweg · P3 · Crew 2 wijk Duin · premium/taxi/pendel",
    file: "/kaarten/pm26-verkeer-1.jpg",
  },
  {
    id: "verkeer-2",
    title: "Verkeer 2 · Personeelsinzet (TTS)",
    subtitle: "Verkeersregelaar-posities 01-36 met aantallen + rollen + looproutes",
    file: "/kaarten/pm26-verkeer-2.jpg",
  },
  {
    id: "verkeer-3",
    title: "Verkeer 3 · Verlichting (TTS)",
    subtitle: "20 lichtmasten op parkeerterreinen en routes",
    file: "/kaarten/pm26-verkeer-3.jpg",
  },
  {
    id: "verkeer-4",
    title: "Verkeer 4 · Calamiteitenroutes (TTS)",
    subtitle: "CR1-CR3 (rood gestippeld) + taxi/pendel/fietsenstalling F1-F2 + K&R",
    file: "/kaarten/pm26-verkeer-4.jpg",
  },
  {
    id: "verkeer-5",
    title: "Verkeer 5 · Pendelbusroute (TTS)",
    subtitle: "Station Almere Poort ⇄ halte Muiderzandweg · K+R-borden afplakken · optionele P3-lus",
    file: "/kaarten/pm26-verkeer-5.jpg",
  },
];
